import { useLayoutEffect, useRef } from 'react';
import { isAutomated, useReducedMotion } from './useReducedMotion';

/** `--ease-out` from the app's globals.css. */
const EASE_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';

/**
 * Rises the incoming tab's content into place whenever `tabKey` changes: the
 * marketing site's format-switch motion, tightened for a tool. The landing
 * page's 14px over ~0.5s is paced for a showcase watched once; tabs here are
 * switched dozens of times a session, so this is 8px over 220ms, inside the
 * 0.2-0.4s range the design principles set.
 *
 * ENTER ONLY. The marketing panels are static and always mounted, so the old
 * one can fade out while the new one fades in. App tabs mount on demand and
 * own live work (podcast audio, the mindmap canvas, polling), so keeping the
 * outgoing tab alive for an exit would play both at once.
 *
 * Web Animations API rather than GSAP, so this package takes no animation
 * dependency. It animates `opacity`, never `visibility`, so the incoming tab
 * stays in the accessibility tree throughout. No `scale`: a transform scale
 * shrinks `getBoundingClientRect`, which the mindmap and PDF viewers measure
 * on mount; a translate does not change a box's size.
 *
 * Skipped on first mount (and StrictMode's re-run of it, by tracking the last
 * key rather than a first-render flag), for reduced motion, and under
 * automation (see `isAutomated`).
 */
export function useTabEnter<T extends HTMLElement = HTMLDivElement>(
  tabKey: string
) {
  const ref = useRef<T>(null);
  const lastKey = useRef<string | null>(null);
  const reduced = useReducedMotion();

  useLayoutEffect(() => {
    const previous = lastKey.current;
    lastKey.current = tabKey;
    const el = ref.current;
    if (previous === null || previous === tabKey || reduced || !el) return;
    if (isAutomated() || typeof el.animate !== 'function') return;

    const animation = el.animate(
      [
        { opacity: 0, transform: 'translateY(8px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration: 220, easing: EASE_OUT }
    );
    return () => animation.cancel();
  }, [tabKey, reduced]);

  return ref;
}
